import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { fetchAdminApplications, deleteAdminApplication } from "../../api/adminData";
import { formatDateOnly } from "../../utils/datetime";
import { formatLanguagesShort } from "../../constants/languages";
import { applicationSourceLabel } from "../../constants/applicationSource";

function describeApplicationListing(row) {
  if (!row.listing_id || !row.listing_title) {
    return { header: "— · Inserat gelöscht", title: null };
  }
  const shortId = row.unit_short_id || "—";
  const roomLabel = row.room_name ? `Room ${row.room_name}` : "Ganze Unit";
  return { header: `${shortId} · ${roomLabel}`, title: row.listing_title };
}

const STATUS_OPTIONS = [
  { value: "", label: "Alle Status" },
  { value: "new", label: "Neu" },
  { value: "reviewing", label: "In Prüfung" },
  { value: "viewing_scheduled", label: "Besichtigung geplant" },
  { value: "accepted", label: "Angenommen" },
  { value: "rejected", label: "Abgelehnt" },
  { value: "archived", label: "Archiviert" },
];

export function getApplicationStatusMeta(status) {
  const s = String(status || "").toLowerCase();
  const base =
    "inline-flex items-center rounded-full border px-2 py-[2px] text-[9px] font-semibold";
  if (s === "new") {
    return {
      label: "Neu",
      pillClass: `${base} border-[rgba(59,130,246,0.35)] bg-[rgba(59,130,246,0.12)] text-[#3b82f6]`,
    };
  }
  if (s === "reviewing") {
    return {
      label: "In Prüfung",
      pillClass: `${base} border-[rgba(245,166,35,0.35)] bg-[rgba(245,166,35,0.12)] text-[#f5a623]`,
    };
  }
  if (s === "viewing_scheduled") {
    return {
      label: "Besichtigung",
      pillClass: `${base} border-[rgba(139,92,246,0.35)] bg-[rgba(139,92,246,0.12)] text-[#8b5cf6]`,
    };
  }
  if (s === "accepted") {
    return {
      label: "Angenommen",
      pillClass: `${base} border-[rgba(61,220,132,0.35)] bg-[rgba(61,220,132,0.14)] text-[#3ddc84]`,
    };
  }
  if (s === "rejected") {
    return {
      label: "Abgelehnt",
      pillClass: `${base} border-[rgba(255,95,109,0.35)] bg-[rgba(255,95,109,0.14)] text-[#ff5f6d]`,
    };
  }
  if (s === "archived") {
    return {
      label: "Archiviert",
      pillClass: `${base} border-slate-500/35 bg-slate-800/90 text-slate-200`,
    };
  }
  if (s === "converted") {
    return {
      label: "Konvertiert",
      pillClass: `${base} border-[rgba(255,122,61,0.35)] bg-[rgba(255,122,61,0.14)] text-[#FF7A3D]`,
    };
  }
  if (s === "failed_upload" || s === "failed_pdf") {
    return {
      label: s === "failed_upload" ? "Upload-Fehler" : "PDF-Fehler",
      pillClass: `${base} border-[rgba(255,95,109,0.35)] bg-[rgba(255,95,109,0.14)] text-[#ff5f6d]`,
    };
  }
  return {
    label: status || "—",
    pillClass: `${base} border-slate-500/30 bg-slate-800/70 text-slate-300`,
  };
}

function displayName(row) {
  const contact = `${row.first_name || ""} ${row.last_name || ""}`.trim();
  if (row.applicant_type === "company" && row.company_name) {
    return contact ? `${row.company_name} (${contact})` : row.company_name;
  }
  return contact || "—";
}

function applicantTypeLabel(type) {
  return type === "company" ? "Firma" : "Privatperson";
}

export default function AdminApplicationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminApplications({
        status: status || undefined,
        q: debouncedSearch || undefined,
        limit: 200,
      });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || "Bewerbungen konnten nicht geladen werden.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [status, debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = useCallback((row) => {
    const name = displayName(row);
    if (!window.confirm(`Bewerbung von ${name} wirklich löschen?`)) return;
    deleteAdminApplication(row.id)
      .then(() => {
        setItems((prev) => prev.filter((r) => r.id !== row.id));
        toast.success("Bewerbung gelöscht.");
      })
      .catch((e) => {
        toast.error(e.message || "Löschen fehlgeschlagen.");
      });
  }, []);

  const counts = useMemo(() => {
    const byStatus = {};
    for (const row of items) {
      const k = String(row.status || "").toLowerCase();
      byStatus[k] = (byStatus[k] || 0) + 1;
    }
    return byStatus;
  }, [items]);

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col gap-1">
        <h1 className="text-2xl font-semibold text-slate-800 dark:text-white">
          Bewerbungen
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Eingegangene WG-Bewerbungen aus dem öffentlichen Formular.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="flex flex-col">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Suche
          </label>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, Firma, E-Mail"
            className="mt-1 h-9 w-64 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-[#FF7A3D] dark:border-white/10 dark:bg-[#141824] dark:text-white"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Status
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="mt-1 h-9 w-52 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-[#FF7A3D] dark:border-white/10 dark:bg-[#141824] dark:text-white"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="ml-auto text-xs text-slate-500 dark:text-slate-400">
          {loading ? "Lade…" : `${items.length} Bewerbung${items.length === 1 ? "" : "en"}`}
          {Object.keys(counts).length > 0 && (
            <span className="ml-2">
              (neu: {counts["new"] || 0}, in Prüfung: {counts["reviewing"] || 0})
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-[14px] border border-black/10 bg-white dark:border-white/[0.07] dark:bg-[#141824]">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:bg-[#1a2030] dark:text-slate-400">
            <tr>
              <th className="px-4 py-3">Eingang</th>
              <th className="px-4 py-3">Bewerber</th>
              <th className="px-4 py-3">Typ</th>
              <th className="px-4 py-3">Inserat</th>
              <th className="px-4 py-3">Einzug</th>
              <th className="px-4 py-3">Mietdauer</th>
              <th className="px-4 py-3">Einkommen</th>
              <th className="px-4 py-3">Ort</th>
              <th className="px-4 py-3">Sprachen</th>
              <th className="px-4 py-3">Quelle</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {!loading && items.length === 0 && (
              <tr>
                <td
                  colSpan={12}
                  className="px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400"
                >
                  Keine Bewerbungen gefunden.
                </td>
              </tr>
            )}
            {items.map((row) => {
              const meta = getApplicationStatusMeta(row.status);
              const obj = describeApplicationListing(row);
              return (
                <tr
                  key={row.id}
                  className="border-t border-black/5 hover:bg-slate-50 dark:border-white/[0.05] dark:hover:bg-[#1a2030]"
                >
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                    <Link
                      to={`/admin/applications/${row.id}`}
                      className="block text-current no-underline"
                    >
                      {formatDateOnly(row.created_at)}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-slate-800 dark:text-white">
                    <Link
                      to={`/admin/applications/${row.id}`}
                      className="block text-current no-underline"
                    >
                      <div className="font-medium">{displayName(row)}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {row.email}
                      </div>
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                    {applicantTypeLabel(row.applicant_type)}
                  </td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                    <div className="font-medium">{obj.header}</div>
                    {obj.title && (
                      <div
                        className="mt-0.5 max-w-[220px] truncate text-xs text-slate-500 dark:text-slate-400"
                        title={obj.title}
                      >
                        {obj.title}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                    {formatDateOnly(row.move_in_date)}
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                    {row.rent_duration || "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                    {row.gross_income || "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                    {row.address_city || "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                    {formatLanguagesShort(row.languages)}
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                    {applicationSourceLabel(row.source)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={meta.pillClass}>{meta.label}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => handleDelete(row)}
                      className="rounded-md p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:text-slate-500 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                      aria-label="Bewerbung löschen"
                      title="Bewerbung löschen"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
